import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, Menu, Moon, Sun, Settings, User, Calendar } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { LanguageSwitcher } from "@/components/shared/language-switcher";
import { useTheme } from "@/components/shared/theme-provider";
import { useTranslation } from "react-i18next";
import { BadeelLogo } from "@/components/shared/badeel-logo";
import { NotificationBadge } from "@/components/notifications/notification-badge";
import { useLocation } from "wouter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [, setLocation] = useLocation();
  const currentLanguage = i18n.language || 'en';
  const isArabic = currentLanguage === 'ar';
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Update date and time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format date and time based on language with Sudan timezone
  const formatDateTime = () => {
    const timezone = 'Africa/Khartoum'; // Sudan timezone
    
    if (isArabic) {
      const dateOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
      };
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: timezone,
      };
      const date = currentDateTime.toLocaleDateString('ar-SA', dateOptions);
      const time = currentDateTime.toLocaleTimeString('ar-SA', timeOptions);
      return `${date} - ${time}`;
    } else {
      const dateOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
      };
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: timezone,
      };
      const date = currentDateTime.toLocaleDateString('en-US', dateOptions);
      const time = currentDateTime.toLocaleTimeString('en-US', timeOptions);
      return `${date} - ${time}`;
    }
  };

  // Debug user data
  console.log('👤 Header user data:', user);

  const handleLogout = async () => {
    console.log('🚪 Header: Logout clicked');
    try {
      // Clear all user data from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('attempted_real_auth');
      
      // Call logout API if available
      await logout();
      
      console.log('🚪 Header: Logout completed, redirecting to login');
      
      // Redirect to login page
      setLocation('/login');
    } catch (error) {
      console.error('🚪 Header: Logout error:', error);
      // Even if logout API fails, clear local data and redirect
      localStorage.clear();
      setLocation('/login');
    }
  };

  const handleProfileSettings = () => {
    console.log('👤 Profile settings clicked');
    // Navigate to profile settings page
    setLocation('/profile-settings');
  };

  const handleSystemSettings = () => {
    console.log('⚙️ System settings clicked');
    // Navigate to system settings page
    setLocation('/settings');
  };

  const handleSwitchToRealLogin = () => {
    
    // Clear mock user data without calling logout API
    localStorage.setItem('attempted_real_auth', 'true');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    
    
    // Force redirect without going through logout process
    window.location.replace('/login');
  };

  const isMockUser = user?.id === 'mock-1';

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 lg:px-6 w-full">
        {/* Left Section */}
        <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 min-w-0 flex-1">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0"
            onClick={onMenuClick}
          >
            <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          {/* Welcome Message with Animated Hand */}
          <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-2 sm:space-x-3' : 'space-x-2 sm:space-x-3'}`}>
            <span 
              className="text-xl sm:text-2xl animate-wave inline-block"
              style={{ transformOrigin: '70% 70%' }}
            >
              👋
            </span>
            <div className="hidden sm:block">
              <div className={`${isArabic ? 'text-right' : 'text-left'}`}>
                <p className={`text-sm sm:text-base font-medium text-foreground`}>
                  {isArabic ? (
                    <>
                      <span className="font-semibold text-primary">{user?.name || user?.email || "المدير"}</span> {t("welcome")}
                    </>
                  ) : (
                    <>
                      {t("welcome")}, <span className="font-semibold text-primary">{user?.name || user?.email || "Admin"}</span>
                    </>
                  )}
                </p>
                {/* Date and Time below admin name */}
                <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-1 justify-end' : 'space-x-1'} mt-0.5`}>
                  <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <p className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap">
                    {formatDateTime()}
                  </p>
                </div>
              </div>
            </div>
            <div className="sm:hidden">
              <div className={`${isArabic ? 'text-right' : 'text-left'}`}>
                <p className={`text-xs font-medium text-foreground`}>
                  {isArabic ? (
                    <>
                      <span className="font-semibold text-primary">{user?.name?.split(' ')[0] || "المدير"}</span> {t("welcome")}
                    </>
                  ) : (
                    <>
                      {t("welcome")}, <span className="font-semibold text-primary">{user?.name?.split(' ')[0] || "Admin"}</span>
                    </>
                  )}
                </p>
                {/* Date and Time below admin name - mobile */}
                <div className={`flex items-center ${isArabic ? 'space-x-reverse space-x-1 justify-end' : 'space-x-1'} mt-0.5`}>
                  <Calendar className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
                  <p className="text-[9px] text-muted-foreground whitespace-nowrap">
                    {formatDateTime()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          {/* Notifications */}
          <NotificationBadge />

          {/* Language Switcher - responsive visibility */}
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>

          {/* Theme Toggle */}
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8 sm:h-9 sm:w-9">
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-1 sm:space-x-2 px-1 sm:px-2 h-8 sm:h-10">
                    <Avatar className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {user?.name?.charAt(0)  }
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left min-w-0">
                      <p className="text-xs sm:text-sm font-medium truncate max-w-20 lg:max-w-32">
                        {user?.name || user?.email || user?.phone }
                      </p>
                      {user?.email && user?.name && (
                        <p className="text-xs text-muted-foreground truncate max-w-20 lg:max-w-32">
                          {user.email}
                        </p>
                      )}
                    </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{t("settings")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleProfileSettings}>
                <User className="mr-2 h-4 w-4" />
                <span>{t("profileSettings") || "Profile Settings"}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSystemSettings}>
                <Settings className="mr-2 h-4 w-4" />
                <span>{t("systemSettings") || "System Settings"}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isMockUser && (
                <DropdownMenuItem onClick={handleSwitchToRealLogin} className="text-blue-600">
                  <User className="mr-2 h-4 w-4" />
                  <span>Switch to Real Login</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <span>{t("logout")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
